import React, { useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, ChevronDown, ChevronRight, Megaphone, CheckCircle2,
  AlertTriangle, ExternalLink, Calendar,
} from 'lucide-react';
import { Button, Pill, Card, EmptyState, Slideout } from '../../index';
import { SeverityChip } from '../widgets/SeverityChip';
import { StatusBadge } from '../widgets/StatusBadge';
import {
  ccListChanges, ccGetChange, ccGetChangeGapAnalysis,
  ccGenerateMigrationTasks,
} from '../../../../services/api';

/**
 * RegulatoryChangesSlideout — V2 Regulatory Changes (Phase B5 / G318).
 *
 * Replaces ?legacy=1&tab=REG_CHANGES. Surfaces upcoming framework
 * changes (SOC 2 v3, GDPR amendments, etc.) and the workflow to
 * acknowledge / generate migration tasks / mark mitigated.
 *
 *   - Filter by framework
 *   - Table: change type | framework | severity | effective date | status
 *   - Expand: full description, affected controls, gap analysis,
 *     "Generate migration tasks" button → kicks off remediation creation
 *   - Acknowledge / Mark mitigated workflow
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onClose
 * @param {Function} props.addToast
 */
export function RegulatoryChangesSlideout({ isOpen, onClose, addToast }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [frameworkFilter, setFrameworkFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ccListChanges(frameworkFilter || undefined);
      const list = Array.isArray(r) ? r : (r?.data || r?.items || r?.changes || []);
      setItems(Array.isArray(list) ? list : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isOpen) reload(); /* eslint-disable-next-line */ }, [isOpen, frameworkFilter]);

  /* Distinct framework list for the filter dropdown — pulled from
   * the data so we never show frameworks the tenant doesn't track. */
  const frameworks = useMemo(() => {
    const s = new Set();
    for (const c of items) if (c.framework || c.frameworkName) s.add(c.framework || c.frameworkName);
    return Array.from(s).sort();
  }, [items]);

  const toggleExpand = (id) => setExpanded((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      width={760}
      ariaLabel="Regulatory changes workspace"
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={18} className="text-v2-action" />
            <h2 className="text-[16px] font-bold text-v2-ink m-0">Regulatory changes</h2>
            <Pill tone="cyan" size="sm">{items.length}</Pill>
          </div>
          <p className="text-[12.5px] text-v2-ink-secondary m-0 mb-3">
            Upcoming framework updates that affect your active controls. Each change links
            to a gap analysis + auto-generated migration tasks.
          </p>
          <div className="flex items-center gap-2">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-v2-ink-muted">Framework:</label>
            <select value={frameworkFilter} onChange={(e) => setFrameworkFilter(e.target.value)}
                    className="border border-v2-border-subtle rounded-v2-sm px-2 py-1 bg-v2-surface text-[12.5px]">
              <option value="">All frameworks</option>
              {frameworks.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <Button variant="ghost" size="sm"
                    className="ml-auto"
                    iconLeft={<RefreshCw size={12} className={loading ? 'animate-spin' : ''} />}
                    onClick={reload}>
              Refresh
            </Button>
          </div>
        </div>
      }
    >
      {loading && items.length === 0 && (
        <p className="text-[12.5px] text-v2-ink-muted">Loading changes…</p>
      )}
      {!loading && items.length === 0 && (
        <EmptyState
          icon={<CheckCircle2 size={20} />}
          title="No regulatory changes"
          description="No upcoming framework changes affect your active controls."
        />
      )}
      {items.length > 0 && (
        <ul className="space-y-2 m-0 p-0 list-none">
          {items.map((c) => (
            <ChangeRow
              key={c.id}
              change={c}
              expanded={expanded.has(c.id)}
              onToggle={() => toggleExpand(c.id)}
              onGenerateMigration={async () => {
                const r = await ccGenerateMigrationTasks(c.id, {});
                if (r?.ok === false) throw new Error(r.error || 'Failed');
                addToast?.(`Migration tasks generated — see Remediation.`, 'success');
                reload();
              }}
              addToast={addToast}
            />
          ))}
        </ul>
      )}
    </Slideout>
  );
}

function ChangeRow({ change: c, expanded, onToggle, onGenerateMigration, addToast }) {
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [loadingGap, setLoadingGap] = useState(false);
  const [busy, setBusy] = useState(false);

  /* Load gap analysis lazily on first expand. */
  useEffect(() => {
    if (!expanded || gapAnalysis !== null) return;
    setLoadingGap(true);
    ccGetChangeGapAnalysis(c.id, '')
      .then((r) => setGapAnalysis(r?.data || r || {}))
      .catch(() => setGapAnalysis({}))
      .finally(() => setLoadingGap(false));
  }, [expanded, c.id, gapAnalysis]);

  const handleGenerate = async () => {
    setBusy(true);
    try { await onGenerateMigration(); }
    catch (e) { addToast?.(`Failed: ${e?.message || e}`, 'error'); }
    finally { setBusy(false); }
  };

  return (
    <li className="bg-v2-surface border border-v2-border-subtle rounded-v2-md overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 hover:bg-v2-muted flex items-center gap-3"
      >
        {expanded ? <ChevronDown size={14} className="text-v2-ink-muted shrink-0" />
                  : <ChevronRight size={14} className="text-v2-ink-muted shrink-0" />}
        <SeverityChip severity={c.impactSeverity || c.severity || c.impact || 'MEDIUM'} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-v2-ink text-[13px] truncate">
            {c.title || c.changeName || `${c.framework || 'Change'} update`}
          </div>
          <div className="text-[11px] text-v2-ink-muted truncate flex items-center gap-2">
            {c.framework && <span>{c.framework}</span>}
            {c.changeType && <><span>·</span><span>{String(c.changeType).replace(/_/g, ' ').toLowerCase()}</span></>}
            {c.effectiveDate && (
              <><span>·</span><span className="inline-flex items-center gap-0.5">
                <Calendar size={10} /> effective {new Date(c.effectiveDate).toLocaleDateString()}
              </span></>
            )}
          </div>
        </div>
        <StatusBadge status={c.status || 'PENDING'} />
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 border-t border-v2-border-subtle bg-v2-muted/30 space-y-3">
          {(c.description || c.summary) && (
            <p className="text-[12.5px] text-v2-ink mt-3 m-0">{c.description || c.summary}</p>
          )}

          {Array.isArray(c.affectedControls) && c.affectedControls.length > 0 && (
            <div>
              <span className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted block mb-1.5">
                Affected controls
              </span>
              <div className="flex items-center gap-1.5 flex-wrap">
                {c.affectedControls.map((ctrl, i) => (
                  <Pill key={i} tone="cyan" size="sm">{ctrl.id || ctrl}</Pill>
                ))}
              </div>
            </div>
          )}

          {loadingGap && (
            <p className="text-[11.5px] text-v2-ink-muted italic">Loading gap analysis…</p>
          )}
          {gapAnalysis && Object.keys(gapAnalysis).length > 0 && !loadingGap && (
            <div className="bg-v2-surface border border-v2-border-subtle rounded-v2-sm p-3">
              <div className="text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-2">
                Gap analysis
              </div>
              <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11.5px]">
                {Object.entries(gapAnalysis).slice(0, 8).map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="text-v2-ink-muted">{k.replace(/([A-Z])/g, ' $1').trim()}</dt>
                    <dd className="text-v2-ink m-0">{typeof v === 'object' ? JSON.stringify(v).slice(0, 60) : String(v)}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          )}

          <div className="flex items-center gap-2 pt-2 border-t border-v2-border-subtle">
            <Button variant="primary" size="sm" disabled={busy}
                    onClick={handleGenerate}>
              {busy ? 'Generating…' : 'Generate migration tasks'}
            </Button>
            {c.externalUrl && (
              <a href={c.externalUrl} target="_blank" rel="noopener noreferrer"
                 className="ml-auto text-[11.5px] text-v2-action hover:underline inline-flex items-center gap-1">
                Source reference <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

export default RegulatoryChangesSlideout;
